'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Cart, CartItem } from '@/lib/shopify/types';
import { isFeeProductHandle } from '@/lib/shopify';

interface CartLine {
  variantId: string;
  quantity: number;
  attributes?: { key: string; value: string }[];
}

// Embroidery (digitising) fees are charged once per (product, side). Each fee
// line is tagged with the parent garment's PRODUCT id (`_linkedProduct`); the
// cascade keeps the fee alive while ANY variant of that product is in the cart
// and removes it the moment the last one leaves.
//
// The link attribute migrated from variant-scoped (`_linkedVariant`, legacy)
// to product-scoped (`_linkedProduct`, current). The resolver reads both so a
// cart created before the change keeps cascading correctly.
export function isFeeLine(line: CartItem): boolean {
  return isFeeProductHandle(line.merchandise.product.handle);
}

export function linkedProductIdOf(
  feeLine: CartItem,
  allLines: readonly CartItem[]
): string | undefined {
  const linkedProduct = feeLine.attributes.find((a) => a.key === '_linkedProduct')?.value;
  if (linkedProduct) return linkedProduct;
  // Legacy: variant-scoped link. Map the variant id back to its owning
  // product by finding any line carrying that variant.
  const linkedVariant = feeLine.attributes.find((a) => a.key === '_linkedVariant')?.value;
  if (!linkedVariant) return undefined;
  const owner = allLines.find((l) => l.merchandise.id === linkedVariant);
  return owner?.merchandise.product.id;
}

export type EmbroiderySide = 'front' | 'back';

// Map a fee line back to the side it covers (front vs back) by reading the
// product handle. Returns null for non-fee lines or unknown handles.
export function feeSideOf(line: CartItem): EmbroiderySide | null {
  const handle = line.merchandise.product.handle;
  if (handle === 'embroidery-digitising-fee-front') return 'front';
  if (handle === 'embroidery-digitising-fee-back') return 'back';
  return null;
}

// Did this garment line opt into embroidery on the given side? Read the flag
// attributes that the add-to-cart components write at add time.
export function variantHasSide(line: CartItem, side: EmbroiderySide): boolean {
  const key = side === 'front' ? '_embroideryFront' : '_embroideryBack';
  return line.attributes.some((a) => a.key === key && a.value === 'true');
}

// Detect duplicate fee lines in a cart. Two fees count as duplicates when they
// share the same (linked product id, fee variant id) — i.e. same product, same
// side. Returns the line ids of the EXTRAS to remove (keeps the first match).
//
// This is the absolute rule: a product never has more than one fee per side.
// `addItems` already dedups on the way in, but this catches:
//   • legacy carts created before the linkage moved from variant- to
//     product-scoped (`_linkedVariant` → `_linkedProduct`),
//   • cross-tab/race conditions where in-memory cart was stale at add-time.
function findDuplicateFeeIds(lines: readonly CartItem[]): string[] {
  const seen = new Set<string>();
  const toRemove: string[] = [];
  for (const line of lines) {
    if (!isFeeLine(line)) continue;
    const productId = linkedProductIdOf(line, lines);
    if (!productId) continue;
    const key = `${productId}|${line.merchandise.id}`;
    if (seen.has(key)) toRemove.push(line.id);
    else seen.add(key);
  }
  return toRemove;
}

interface CartContextType {
  cart: Cart | null;
  isLoading: boolean;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (variantId: string, quantity?: number, attributes?: { key: string; value: string }[]) => Promise<void>;
  addItems: (lines: CartLine[]) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  applyCartDiscount: (discountCode?: string) => Promise<string | null>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_ID_KEY = 'shopify_cart_id';

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  // Initialize cart on mount
  useEffect(() => {
    const initializeCart = async () => {
      const cartId = localStorage.getItem(CART_ID_KEY);

      if (cartId) {
        try {
          const response = await fetch(`/api/cart?cartId=${cartId}`);
          const data = await response.json();

          if (data.cart) {
            // Dedup cleanup is handled reactively by the cart-watcher effect
            // below — it triggers on every cart change, including this initial
            // load, so we only need to set the raw cart here.
            setCart(data.cart);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error fetching cart:', error);
        }
      }

      // Create new cart if none exists
      try {
        const response = await fetch('/api/cart', { method: 'POST' });
        const data = await response.json();

        if (data.cart) {
          localStorage.setItem(CART_ID_KEY, data.cart.id);
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error creating cart:', error);
      }

      setIsLoading(false);
    };

    initializeCart();
  }, []);

  // Reactive fee-dedup watcher. The invariant is absolute: a product never
  // has more than one digitising fee per side. If the cart ever ends up with
  // duplicates (legacy lines pre-dedup, cross-tab races, network blips), this
  // effect detects it on the next render and removes the extras automatically.
  // Self-clearing: once the duplicate is gone the next cart state has no dupes
  // and the effect short-circuits.
  useEffect(() => {
    if (!cart) return;
    const dupes = findDuplicateFeeIds(cart.lines);
    if (dupes.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/cart/remove', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartId: cart.id, lineIds: dupes }),
        });
        const data = await res.json();
        if (!cancelled && data.cart) setCart(data.cart);
      } catch (e) {
        console.error('Fee dedup cleanup failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [cart]);

  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  // Apply post-mutation fee dedup cleanup. Resolves to the cleaned cart (or
  // the input cart if no dupes were found / cleanup failed). The dedup at
  // add-time prevents new dupes; this is the safety net that keeps the
  // invariant true even when in-memory state is stale or a legacy cart slips
  // a duplicate past the add-time check.
  const cleanFeeDupes = useCallback(async (incomingCart: Cart): Promise<Cart> => {
    const dupes = findDuplicateFeeIds(incomingCart.lines);
    if (dupes.length === 0) return incomingCart;
    try {
      const res = await fetch('/api/cart/remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cartId: incomingCart.id, lineIds: dupes }),
      });
      const data = await res.json();
      return data.cart ?? incomingCart;
    } catch (e) {
      console.error('Fee dedup cleanup failed:', e);
      return incomingCart;
    }
  }, []);

  const addItem = useCallback(
    async (variantId: string, quantity: number = 1, attributes?: { key: string; value: string }[]) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            variantId,
            quantity,
            attributes,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          const cleaned = await cleanFeeDupes(data.cart);
          setCart(cleaned);
          openCart();
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
      }

      setIsLoading(false);
    },
    [cart, openCart, cleanFeeDupes]
  );

  const addItems = useCallback(
    async (lines: CartLine[]) => {
      if (!cart || lines.length === 0) return;

      // Drop any fee lines whose (linked product, fee variant) is already
      // covered by an existing fee in the cart. This is what keeps the
      // embroidery fee one-time-per-product when the same product is added in
      // multiple sizes — Shopify alone would treat the second add as a fresh
      // line because the attributes (still containing the _linkedProduct id)
      // are identical, but only after dedup. Filtering here is the cleanest
      // place: the cart context already knows the current state.
      const existingFeeKeys = new Set<string>();
      for (const line of cart.lines) {
        if (!isFeeLine(line)) continue;
        const productId = linkedProductIdOf(line, cart.lines);
        if (productId) existingFeeKeys.add(`${productId}|${line.merchandise.id}`);
      }
      const filteredLines = lines.filter((incoming) => {
        const linkedProduct = incoming.attributes?.find((a) => a.key === '_linkedProduct')?.value;
        if (!linkedProduct) return true; // not a fee (or non-fee with no _linkedProduct)
        return !existingFeeKeys.has(`${linkedProduct}|${incoming.variantId}`);
      });
      if (filteredLines.length === 0) {
        // Everything was deduped — nothing to send. Still open the cart so
        // the customer sees their existing items.
        openCart();
        return;
      }

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/add', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lines: filteredLines,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          const cleaned = await cleanFeeDupes(data.cart);
          setCart(cleaned);
          openCart();
        }
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }

      setIsLoading(false);
    },
    [cart, openCart, cleanFeeDupes]
  );

  const updateItem = useCallback(
    async (lineId: string, quantity: number) => {
      if (!cart) return;

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lineId,
            quantity,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error updating cart:', error);
      }

      setIsLoading(false);
    },
    [cart]
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      if (!cart) return;

      // Cascade rules — side-aware (front and back are tracked independently):
      //   - GARMENT removed: for each fee tied to the same product, cascade
      //     ONLY when no OTHER remaining variant of that product still has
      //     the matching side flag set. Surviving variants without the flag
      //     don't keep the fee alive.
      //   - FEE removed directly: refuse while any variant of the linked
      //     product still has the matching side flag. The drawer's "Required"
      //     badge mirrors this; this guard backstops other entry points.
      //   - Legacy garments/fees (pre-Phase-3, no side flag): fall back to
      //     product-level "any variant remains" rule.
      const lineToRemove = cart.lines.find((l) => l.id === lineId);
      const idsToRemove: string[] = [lineId];
      if (lineToRemove) {
        if (isFeeLine(lineToRemove)) {
          const linkedProductId = linkedProductIdOf(lineToRemove, cart.lines);
          const side = feeSideOf(lineToRemove);
          if (linkedProductId) {
            const stillNeeded = side
              ? cart.lines.some(
                  (l) =>
                    l.id !== lineId &&
                    !isFeeLine(l) &&
                    l.merchandise.product.id === linkedProductId &&
                    variantHasSide(l, side)
                )
              : cart.lines.some(
                  (l) =>
                    l.id !== lineId &&
                    !isFeeLine(l) &&
                    l.merchandise.product.id === linkedProductId
                );
            if (stillNeeded) return;
          }
        } else {
          // Removing a garment. For each fee tied to its product, check
          // whether any OTHER remaining variant still wants that side.
          const removedProductId = lineToRemove.merchandise.product.id;
          for (const line of cart.lines) {
            if (line.id === lineId) continue;
            if (!isFeeLine(line)) continue;
            const feeProductId = linkedProductIdOf(line, cart.lines);
            if (feeProductId !== removedProductId) continue;
            const side = feeSideOf(line);
            const otherStillNeeds = side
              ? cart.lines.some(
                  (l) =>
                    l.id !== lineId &&
                    l.id !== line.id &&
                    !isFeeLine(l) &&
                    l.merchandise.product.id === removedProductId &&
                    variantHasSide(l, side)
                )
              : cart.lines.some(
                  (l) =>
                    l.id !== lineId &&
                    l.id !== line.id &&
                    !isFeeLine(l) &&
                    l.merchandise.product.id === removedProductId
                );
            if (!otherStillNeeds) idsToRemove.push(line.id);
          }
        }
      }

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/remove', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            lineIds: idsToRemove,
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }
      } catch (error) {
        console.error('Error removing from cart:', error);
      }

      setIsLoading(false);
    },
    [cart]
  );

  const applyCartDiscount = useCallback(
    async (discountCode?: string): Promise<string | null> => {
      if (!cart) return 'Cart is not ready yet.';

      setIsLoading(true);

      try {
        const response = await fetch('/api/cart/discount', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cartId: cart.id,
            discountCode: discountCode || '',
          }),
        });

        const data = await response.json();

        if (data.cart) {
          setCart(data.cart);
        }

        if (!response.ok) {
          return typeof data.error === 'string' ? data.error : 'Discount could not be applied.';
        }

        return null;
      } catch (error) {
        console.error('Error updating discount:', error);
        return 'Discount could not be updated.';
      } finally {
        setIsLoading(false);
      }
    },
    [cart]
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        isLoading,
        isOpen,
        openCart,
        closeCart,
        addItem,
        addItems,
        updateItem,
        removeItem,
        applyCartDiscount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}
